import type { SystemProps } from '../system'
import type { LabelProps } from './label.types'

import { omitThemingProps, styled, useStyleConfig, vui } from '../core'
import Icon from '../icon'
import T from '../t'
import Tooltip from '../tooltip'
import { cs } from '../utils'

export const LabelBase = styled.labelBox`
  display: block;
  color: sandstone.10;
  width: 100%;

  &[aria-disabled='true'] {
    cursor: not-allowed;
  }
`

type LabelContentProps = Pick<LabelProps, 'text' | 'markAsOptional' | 'optionalText' | 'tooltipText'> & {
  helperStyles: SystemProps
}

const LabelContent = ({
  text,
  markAsOptional,
  optionalText = '(optional)',
  tooltipText,
  helperStyles,
}: LabelContentProps) => {
  const textProps = { variant: 'label' as const, fontSize: 'inherit' as const }
  const helperProps = { ...textProps, ...helperStyles }

  return (
    <>
      <T {...textProps}>{text}</T>
      {!!text && markAsOptional && <T {...helperProps}>{optionalText}</T>}
      {!!text && !!tooltipText && (
        <T {...helperStyles}>
          <Tooltip text={tooltipText}>
            <Icon color="seaBlue.28" mb="-3px" name="uiInfoCircle" size="xs" role="button" />
          </Tooltip>
        </T>
      )}
    </>
  )
}

/** Displays a label element, optionally with helper text, an optional indicator, and an info tooltip. */
export const Label = vui<'label', LabelProps>((props, ref) => {
  const { children, className, markAsOptional, optionalText, tooltipText, text, ...rest } = omitThemingProps(props)
  const styles = useStyleConfig('Label', props)

  return (
    <T as={LabelBase} className={cs('vui-label', className)} ref={ref} {...rest}>
      {children ?? (
        <LabelContent
          helperStyles={styles.helper}
          markAsOptional={markAsOptional}
          optionalText={optionalText}
          text={text}
          tooltipText={tooltipText}
        />
      )}
    </T>
  )
})

Label.displayName = 'Label'
export default Label
